import { useDisclosure } from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { PinnedItemIcon } from 'components/icons';
import { OptionsMenuItem } from 'components/OptionsMenu/OptionsMenuItem';
import React from 'react';
import { graphql, useFragment, useMutation } from 'utils/graphClient';
import { PinConfirmation } from 'apps/TenantApp/screens/Space/screens/Activity/components/Announcements/components/PinConfirmation';
import { PinOption_pinAnnouncement_Mutation } from './__generated__/PinOption_pinAnnouncement_Mutation.graphql';
import { PinOption_unpinAnnouncement_Mutation } from './__generated__/PinOption_unpinAnnouncement_Mutation.graphql';
import { PinOption_node$key } from './__generated__/PinOption_node.graphql';

const fragment = graphql`
  fragment PinOption_node on Node {
    __typename
    id
    ... on Announcement {
      pinned
      canPin {
        value
      }
      canUnpin {
        value
      }
    }
  }
`;

const pinAnnouncementMutation = graphql`
  mutation PinOption_pinAnnouncement_Mutation($announcementId: ID!) {
    pinAnnouncement(input: { announcementId: $announcementId }) {
      announcementPinned {
        id
        pinned
      }
      space {
        pinnedAnnouncements: announcements(pinnedOnly: true, first: 2) {
          __id
          ...AnnouncementsList_announcements
        }
      }
    }
  }
`;

export const unpinAnnouncementMutation = graphql`
  mutation PinOption_unpinAnnouncement_Mutation($announcementId: ID!) {
    unpinAnnouncement(input: { announcementId: $announcementId }) {
      announcementUnpinned {
        id
        pinned
      }
      space {
        pinnedAnnouncements: announcements(pinnedOnly: true, first: 2) {
          __id
          ...AnnouncementsList_announcements
        }
      }
    }
  }
`;

interface PinOptionProps {
  node: PinOption_node$key;
}

export const PinOption = ({ node }: PinOptionProps) => {
  const item = useFragment(fragment, node);
  const { isOpen, onClose, onOpen } = useDisclosure();

  const [pinAnnouncement, isPinLoading] =
    useMutation<PinOption_pinAnnouncement_Mutation>(pinAnnouncementMutation);
  const [unpinAnnouncement, isUnpinLoading] =
    useMutation<PinOption_unpinAnnouncement_Mutation>(
      unpinAnnouncementMutation,
    );

  const onPin = () => {
    pinAnnouncement({
      variables: { announcementId: item.id },
      onCompleted: onClose,
    });
  };

  const onUnpin = () => {
    unpinAnnouncement({
      variables: { announcementId: item.id },
      onCompleted: onClose,
    });
  };

  const showPinOption = item.__typename === 'Announcement';
  if (!showPinOption) {
    return null;
  }

  if (item.pinned) {
    if (!item.canUnpin?.value) {
      return null;
    }
    return (
      <>
        <OptionsMenuItem
          icon={<PinnedItemIcon />}
          onClick={onOpen}
        >{t`Unpin announcement`}</OptionsMenuItem>
        <PinConfirmation
          isLoading={isUnpinLoading}
          pinned={item.pinned}
          isOpen={isOpen}
          onClose={onClose}
          onConfirmation={onUnpin}
        />
      </>
    );
  }

  if (!item.canPin?.value) {
    return null;
  }

  return (
    <>
      <OptionsMenuItem
        icon={<PinnedItemIcon />}
        onClick={onOpen}
      >{t`Pin announcement`}</OptionsMenuItem>
      <PinConfirmation
        isLoading={isPinLoading}
        pinned={!!item.pinned}
        isOpen={isOpen}
        onClose={onClose}
        onConfirmation={onPin}
      />
    </>
  );
};
